import { createHttpClient } from "@utils/httpClient";
import type {
  CorrectionCreateResponse,
  CorrectionView,
  RecentCorrection,
} from "@models/corrections";

const client = createHttpClient(import.meta.env.VITE_REVIEWS_API_URL ?? "");

const path = (kpi: string, id: string, suffix = "") =>
  `/corrections/${encodeURIComponent(kpi)}/${encodeURIComponent(id)}${suffix}`;

/** Correction lists (PBI #747204). */
export const correctionsApi = {
  /** New correction id + presigned PUT URL for the file. */
  create(kpi: string, filename: string): Promise<CorrectionCreateResponse> {
    return client.makeAuthenticatedRequest("/corrections", {
      method: "POST",
      body: JSON.stringify({ kpi, filename }),
    });
  },

  async uploadFile(url: string, file: File): Promise<void> {
    const response = await fetch(url, { method: "PUT", body: file });
    if (!response.ok) {
      throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
    }
  },

  submit(kpi: string, id: string, filename: string): Promise<{ state: string }> {
    return client.makeAuthenticatedRequest(path(kpi, id, "/submit"), {
      method: "POST",
      body: JSON.stringify({ filename }),
    });
  },

  get(kpi: string, id: string): Promise<CorrectionView> {
    return client.makeAuthenticatedRequest(path(kpi, id));
  },

  saveChoices(kpi: string, id: string, choices: Record<string, string>): Promise<{ saved: number }> {
    return client.makeAuthenticatedRequest(path(kpi, id, "/choices"), {
      method: "PUT",
      body: JSON.stringify({ choices }),
    });
  },

  overview(kpi: string, id: string): Promise<{ url: string; filename: string }> {
    return client.makeAuthenticatedRequest(path(kpi, id, "/overview"));
  },

  approve(kpi: string, id: string): Promise<{ state: string }> {
    return client.makeAuthenticatedRequest(path(kpi, id, "/approve"), { method: "POST" });
  },

  reject(kpi: string, id: string): Promise<{ state: string }> {
    return client.makeAuthenticatedRequest(path(kpi, id, "/reject"), { method: "POST" });
  },

  discard(kpi: string, id: string): Promise<{ state: string }> {
    return client.makeAuthenticatedRequest(path(kpi, id, "/discard"), { method: "POST" });
  },

  recent(kpi: string): Promise<{ corrections: RecentCorrection[] }> {
    return client.makeAuthenticatedRequest(`/corrections/recent?kpi=${encodeURIComponent(kpi)}`);
  },
};
